import moment from 'moment-timezone';

import React, { useState, useEffect, useRef } from 'react';
import { Card, Label } from '@blueprintjs/core';
import { Form, FormInstance } from 'blueprint-form';
import { DateRangeInput3 } from '@blueprintjs/datetime2';
import HighCharts from 'highcharts';
import addExporting from 'highcharts/modules/exporting';
//import addAccessibility from 'highcharts/modules/accessibility';
import HighChartsReact from 'highcharts-react-official';

addExporting(HighCharts);
//addAccessibility(HighCharts);

import {
  ISession,
  SessionTransformer,
  useGetSessionsByRange
} from '../../../libs/session';
import { GetCurrentTZ } from '../../../libs/time';
import ExportGraphData from './export';
import { PostJSON } from '../../../libs/request';
import { CHART } from '../../../config/colors';

export interface GraphInstanceProps {
  params?: any;
}

interface TimeRange {
  start: Date;
  end: Date;
}

export type GraphOptions = HighCharts.Options;
export type GraphSeries =
  | HighCharts.SeriesLineOptions
  | HighCharts.SeriesColumnOptions
  | HighCharts.SeriesSplineOptions
  | HighCharts.SeriesPieOptions;
export type GraphPoint = HighCharts.PointOptionsObject;
export type GraphPlotband = HighCharts.AxisPlotBandsOptions;

export type DrawFunction<T extends ISession, S = any> = (
  sessions: T[],
  currentOptions: GraphOptions,
  settings?: S
) => GraphOptions;

export interface GraphProps<T extends ISession, S = any> {
  name: string;
  config: Omit<GraphOptions, 'series'>;
  chartReflowTrigger?: boolean;
  draw: DrawFunction<T, S>;
  sessions: T[];
  settings?: S;
  addGraphInstance?: (graph: any) => void;
}

export type GraphBlueprint<T extends ISession, S = any> = Omit<
  GraphProps<T, S>,
  'sessions'
>;

const Graph = <T extends ISession, S = any>({
  config,
  chartReflowTrigger,
  draw,
  sessions,
  settings,
  addGraphInstance
}: GraphProps<T, S>): JSX.Element => {
  const chartComponent = useRef(null);
  const [options, setOptions] = useState<GraphOptions>({
    chart: {
      height: 250
    },
    colors: [CHART.SERIES, CHART.SERIES, CHART.SERIES],
    ...config,
    /**
    plotOptions: {
      series: {
        lineWidth: 3,
        marker: {
          radius: 6
        }
      }
    },
    xAxis: {
      labels: {
        style: {
          color: '#211e1f',
          fontSize: '14'
        }
      }
    },
    yAxis: {
      labels: {
        style: {
          color: '#211e1f',
          fontSize: '14'
        }
      }
    },
    */
    title: {
      text: null
    },
    credits: {
      enabled: false
    },
    exporting: {
      enabled: true,
      url: 'https://hcexport.lightbeamhealth.com/',
      buttons: {
        contextButton: {
          verticalAlign: 'bottom'
        }
      }
    }
  });
  useEffect(() => {
    setOptions({
      ...options,
      ...draw(sessions, options, settings)
    });
  }, [sessions, settings]);
  /**
   * Every time the chart reflow trigger
   * changes, we should reflow the chart
   */
  useEffect(() => {
    const chart = chartComponent.current.chart;
    if (chart) {
      chart.reflow();
    }
  }, [chartReflowTrigger]);
  return (
    <HighChartsReact
      ref={chartComponent}
      highcharts={HighCharts}
      options={options}
      callback={addGraphInstance}
    />
  );
};

export interface SessionGraphsProps<
  T extends ISession,
  R extends ISession = never
> {
  url: string;
  chartReflowTrigger?: boolean;
  params?: any;
  graphs: GraphBlueprint<T>[];
  transformer?: SessionTransformer<R, T>;
  programId?: number | null;
}

const SessionGraphs = <
  T extends ISession,
  R extends ISession = never,
  S = any
>({
  url,
  chartReflowTrigger,
  params,
  graphs,
  transformer,
  programId
}: SessionGraphsProps<T, R>): JSX.Element => {
  const initialStart = moment().subtract(2, 'weeks').startOf('day').toDate();
  const initialEnd = moment().endOf('day').toDate();
  const form = new FormInstance<TimeRange>({
    start: initialStart,
    end: initialEnd
  });
  const [graphInstances, setGraphInstances] = useState([]);
  const [start, setStart] = useState<Date>(initialStart);
  const [end, setEnd] = useState<Date>(initialEnd);
  const [minStart, setMinStart] = useState<Date | null>(null);
  const tz = GetCurrentTZ();
  const onRangeChange = ({ start, end }: TimeRange) => {
    setStart(start);
    setEnd(end);
  };

  // Populate the graph instance
  const addGraphInstance = (graph: any) => {
    setGraphInstances(graphInstances.concat(graph));
  };

  // Fetch earliest allowed start (minStart) for the program
  useEffect(() => {
    const app = params?.app;
    const patient = params?.patient;
    if (!app || !patient) {
      setMinStart(null);
      return;
    }
    PostJSON<{ status: 'OK' | 'FAIL'; minStart?: string | null }>({
      url: '/data/export_limits/',
      params: {
        app,
        patientId: patient,
        programId: programId || null,
        tz
      }
    }).then((resp: { status: 'OK' | 'FAIL'; minStart?: string | null }) => {
      if (resp && resp.status === 'OK' && 'minStart' in resp && resp.minStart) {
        const min = moment.tz(resp.minStart, tz).startOf('day').toDate();
        setMinStart(min);
        setStart((prev) => {
          if (prev < min) {
            return min;
          }
          return prev;
        });
      } else {
        setMinStart(null);
      }
    });
  }, [params?.app, params?.patient, programId, tz]);

  // Fetch the sessions by range
  const { sessions, settings } = useGetSessionsByRange<T, R, S>(
    url,
    {
      start: start,
      end: end,
      tz: tz,
      ...params
    },
    transformer,
    graphs.length === 0
  );

  return (
    <>
      <Form className="mb-8" form={form} onChange={onRangeChange}>
        <Label>Date Range</Label>
        <div className="flex align-center">
          <DateRangeInput3
            formatDate={(date) => moment.tz(date, tz).format('YYYY-MM-DD')}
            parseDate={(str) => {
              const m = moment.tz(str, tz);
              return m.isValid() ? m.toDate() : new Date(NaN);
            }}
            value={[minStart && start < minStart ? minStart : start, end]}
            onChange={(range) => {
              const [s, e] = range;
              if (!s || !e) return;
              setStart(s);
              setEnd(e);
            }}
            minDate={minStart || undefined}
            maxDate={moment().endOf('day').toDate()}
            endInputProps={{ fill: true }}
            startInputProps={{ fill: true }}
            popoverProps={{ usePortal: true }}
          />
        </div>
      </Form>
      {graphs.length > 0 && (
        <Card>
          {graphs.map((graph, i) => (
            <div key={i}>
              <Graph<T, S>
                sessions={sessions}
                chartReflowTrigger={chartReflowTrigger}
                settings={settings}
                addGraphInstance={addGraphInstance}
                {...graph}
              />
            </div>
          ))}
        </Card>
      )}
      <div className="mt-8">
        <ExportGraphData
          appKey={params?.app}
          patientId={params?.patient}
          graphs={graphInstances}
          setGraphInstances={setGraphInstances}
          start={start}
          end={end}
          programId={programId}
        />
      </div>
    </>
  );
};

export default SessionGraphs;
